'use client';

import { useState, useMemo, useCallback } from 'react';
import { FindingEditDrawer } from '../../../../findings/FindingEditDrawer';
import { EnhancedEditFindingsModal } from '../../EnhancedEditFindingsModal';
import { useFindingSearchContext } from '../../../finding-search';
import {
  findingToolHitlConfig,
  findingsActionHitlConfig,
} from '../../tools/finding/hitl-config';
import { useTranslations } from 'next-intl';
import {
  convertToEnrichedFinding,
  convertToFindingToolInput,
} from '../utils/finding-converters';
import { Spinner } from '../../../../../components/spinner';
import type { FindingData } from '../types';
import type { InternalEnrichedFinding } from '../../../../../types';
import type { EditableFields } from '../../../../findings/finding-editable-fields';
import type { EditContentProps } from '../../tool-config-types';

export function FindingEditContentWrapper({
  onClose,
  onSubmit,
  onApprove,
  onReject,
  toolName,
  args,
  extractedData = [],
  defaultCvssVersion: defaultCvssVersionProp,
  onDraftArgsChange,
}: EditContentProps) {
  const tCommon = useTranslations('designSystem.common');
  const {
    onSearchSignals,
    onSearchAssets,
    defaultCvssVersion: contextCvssVersion,
    cvssEnvironmentalRequirements,
  } = useFindingSearchContext();
  const defaultCvssVersion = defaultCvssVersionProp ?? contextCvssVersion;

  const [findingDrawerMode, setFindingDrawerMode] = useState<'view' | 'edit'>(
    'edit',
  );

  const hitlConfig =
    toolName === 'finding-tool'
      ? findingToolHitlConfig
      : findingsActionHitlConfig;

  const singleFinding =
    extractedData.length === 1 ? (extractedData[0] as FindingData) : undefined;

  const editingFinding = useMemo<InternalEnrichedFinding | null>(() => {
    if (!singleFinding) {
      return null;
    }

    return convertToEnrichedFinding(singleFinding);
  }, [singleFinding]);

  const handleDrawerSave = async (
    _findingId: string,
    updates: Partial<EditableFields>,
  ): Promise<{ success: boolean; error?: string }> => {
    if (!editingFinding) {
      return { success: false, error: 'No finding to edit' };
    }

    if (Object.keys(updates).length === 0 && onApprove) {
      onApprove();
      onClose();

      return { success: true };
    }

    const findingToolInput = convertToFindingToolInput({
      original: editingFinding,
      updates,
      originalArgs: args,
    });

    onSubmit(findingToolInput);
    onClose();

    return { success: true };
  };

  const handleDraftUpdatesChange = useCallback(
    (updates: Partial<EditableFields> | null) => {
      if (!onDraftArgsChange) {
        return;
      }

      if (!updates || !editingFinding) {
        onDraftArgsChange(null);

        return;
      }

      onDraftArgsChange(
        convertToFindingToolInput({
          original: editingFinding,
          updates,
          originalArgs: args,
        }),
      );
    },
    [onDraftArgsChange, editingFinding, args],
  );

  const handleModalSubmit = (editedFindings: FindingData[]) => {
    if (!hitlConfig) return;
    const wrappedData = hitlConfig.wrapEditedData(editedFindings);

    onSubmit(wrappedData);
    onClose();
  };

  const handleModalApprove = () => {
    onApprove?.();
    onClose();
  };

  if (extractedData.length === 1) {
    if (!editingFinding) {
      return (
        <div className="flex justify-center p-6">
          <Spinner size="sm" />
        </div>
      );
    }

    return (
      <FindingEditDrawer
        finding={editingFinding}
        open={true}
        onClose={onClose}
        onSave={handleDrawerSave}
        mode={findingDrawerMode}
        onModeChange={setFindingDrawerMode}
        onSearchSignals={onSearchSignals}
        onSearchAssets={onSearchAssets}
        renderMode="inline"
        allowSubmitWithoutChanges={Boolean(onApprove)}
        submitLabel={tCommon('create')}
        unchangedLabel={tCommon('noChanges')}
        onReject={onReject}
        {...(defaultCvssVersion ? { defaultCvssVersion } : {})}
        cvssSection="hidden"
        environmentalRequirements={cvssEnvironmentalRequirements}
        owaspRiskEditMode="deferred"
        onDraftUpdatesChange={handleDraftUpdatesChange}
      />
    );
  }

  return (
    <EnhancedEditFindingsModal
      isOpen={true}
      onClose={onClose}
      onSubmit={handleModalSubmit}
      findings={extractedData as FindingData[]}
      actionName={toolName}
      renderMode="inline"
      allowSubmitWithoutChanges={Boolean(onApprove)}
      onApprove={handleModalApprove}
      onReject={onReject}
    />
  );
}
